/**
 * Sprite caching service to optimize Pokémon sprite loading
 * and reduce redundant API calls
 */

// In-memory cache for sprites
const spriteCache = new Map();

// In-flight requests cache to prevent duplicate requests
const inFlightRequests = new Map();

/**
 * Get a Pokémon sprite URL with caching
 * @param {string} pokemonName - The name of the Pokémon
 * @param {function} fetchFunction - The function to fetch the sprite if not cached
 * @returns {Promise<string>} - A promise that resolves to the sprite URL
 */
export const getCachedSprite = async (pokemonName, fetchFunction) => {
  if (!pokemonName) return null;
  
  // Normalize the name for consistent caching
  const normalizedName = pokemonName.toLowerCase().trim();
  
  // Return from cache if available
  if (spriteCache.has(normalizedName)) {
    return spriteCache.get(normalizedName);
  }
  
  // Check if there's already an in-flight request for this Pokémon
  if (inFlightRequests.has(normalizedName)) {
    return inFlightRequests.get(normalizedName);
  }
  
  // Create a new request promise
  const request = fetchFunction(normalizedName)
    .then(url => {
      // Add to cache when resolved
      spriteCache.set(normalizedName, url);
      // Remove from in-flight requests
      inFlightRequests.delete(normalizedName);
      return url;
    })
    .catch(error => {
      // Remove from in-flight requests on error
      inFlightRequests.delete(normalizedName);
      throw error;
    });
  
  // Store the request promise to deduplicate concurrent requests
  inFlightRequests.set(normalizedName, request);
  
  return request;
};

/**
 * Batch load multiple Pokémon sprites efficiently
 * @param {string[]} pokemonNames - Array of Pokémon names
 * @param {function} fetchFunction - The function to fetch a single sprite
 * @returns {Promise<string[]>} - A promise that resolves to an array of sprite URLs
 */
export const batchLoadSprites = async (pokemonNames, fetchFunction) => {
  if (!pokemonNames || !pokemonNames.length) return [];
  
  // Make all requests in parallel using the cached function
  const promises = pokemonNames.map(name => 
    getCachedSprite(name, fetchFunction)
  );
  
  return Promise.all(promises);
};

/**
 * Clear the sprite cache (useful for testing or memory management)
 */
export const clearSpriteCache = () => {
  spriteCache.clear();
  inFlightRequests.clear();
};

export default {
  getCachedSprite,
  batchLoadSprites,
  clearSpriteCache
};